import * as Sentry from '@sentry/browser';
import { logger } from './logger';

// Severity level type
type SeverityLevel = 'fatal' | 'error' | 'warning' | 'log' | 'info' | 'debug';

/**
 * Utility for manually reporting errors and capturing user feedback
 */
export const errorReporting = {
  /**
   * Report an error to Sentry
   * @param error The error to report
   * @param context Additional context information
   */
  captureException: (error: unknown, context?: Record<string, unknown>): string => {
    // Log error locally
    logger.error(error, context);

    // Report to Sentry if it's a real error
    if (error instanceof Error) {
      return Sentry.captureException(error, {
        extra: context,
      });
    } else {
      return Sentry.captureMessage(String(error), {
        level: 'error',
        extra: context,
      });
    }
  },

  /**
   * Report a warning to Sentry (for non-critical issues)
   * @param message Warning message
   * @param context Additional context information
   */
  captureWarning: (message: string, context?: Record<string, unknown>): string => {
    // Log warning locally
    logger.warn(message, context);

    // Report to Sentry
    return Sentry.captureMessage(message, {
      level: 'warning',
      extra: context,
    });
  },

  /**
   * Set user information for error reports
   * Call this when a user logs in to associate errors with the user
   * @param user User information
   */
  setUser: (user: { id: string; email?: string; username?: string }): void => {
    Sentry.setUser(user);
  },

  /**
   * Clear user information when a user logs out
   */
  clearUser: (): void => {
    Sentry.setUser(null);
  },

  /**
   * Add breadcrumb to provide context for error reports
   * Use this to track user actions or application state changes
   * @param breadcrumb Breadcrumb information
   */
  addBreadcrumb: (breadcrumb: {
    category?: string;
    message: string;
    level?: SeverityLevel;
    data?: Record<string, unknown>;
  }): void => {
    Sentry.addBreadcrumb({
      category: breadcrumb.category || 'custom',
      message: breadcrumb.message,
      level: (breadcrumb.level || 'info') as SeverityLevel,
      data: breadcrumb.data,
    });
  },

  /**
   * Set extra context for all future errors
   * @param key Context key
   * @param value Context value
   */
  setContext: (key: string, value: Record<string, unknown>): void => {
    Sentry.setContext(key, value);
  },

  /**
   * Show Sentry user feedback dialog to collect additional information
   * Call this after capturing an exception
   * @param eventId The ID returned from captureException
   */
  showReportDialog: (eventId: string): void => {
    Sentry.showReportDialog({ eventId });
  },
};

export default errorReporting;